// El informe visual (03-11, Pau: «me parece bien la idea de dar respuestas estructuradas y visuales, pero así como está
// ahora [PDF que cada agente maquetaba a su manera y se abría en otra pestaña], basura»). El agente escribe Markdown y
// unos pocos bloques; la oficina pone el diseño (uno, el de Venuz) y lo abre dentro del chat. La gramática la valida el
// servidor del agente antes de aceptar el envío (backend/src/oficina/informe.ts, misma gramática): aquí se pinta con
// tolerancia (un bloque roto se ve como texto, nunca rompe el informe).

/** Los bloques con nombre que entiende el informe (```<nombre> … ```). */
export const AVISOS = ['recomendacion', 'decision', 'riesgo', 'bien', 'nota'] as const;
export type Aviso = (typeof AVISOS)[number];

export interface Cifra {
  valor: string;
  etiqueta: string;
  nota?: string;
  tono?: 'bien' | 'mal';
}

/** ```cifras: una por línea, «valor | etiqueta | nota | bien|mal» (nota y tono, opcionales). */
export function leerCifras(texto: string): Cifra[] {
  return texto
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l): Cifra => {
      const [valor = '', etiqueta = '', nota, tono] = l.split('|').map((p) => p.trim());
      return {
        valor,
        etiqueta,
        ...(nota ? { nota } : {}),
        ...(tono === 'bien' || tono === 'mal' ? { tono } : {}),
      };
    })
    .filter((c) => c.valor);
}

export interface Grafica {
  tipo: 'barras' | 'barras-horizontales' | 'lineas';
  titulo?: string;
  unidad?: string;
  etiquetas: string[];
  series: Array<{ nombre: string; valores: number[] }>;
}

/** ```grafica: JSON. null si no se entiende (se enseña el texto tal cual). */
export function leerGrafica(texto: string): Grafica | null {
  try {
    const g = JSON.parse(texto) as Partial<Grafica>;
    if (g.tipo !== 'barras' && g.tipo !== 'barras-horizontales' && g.tipo !== 'lineas') return null;
    if (!Array.isArray(g.etiquetas) || !Array.isArray(g.series) || g.series.length === 0 || g.series.length > 4) return null;
    const etiquetas = g.etiquetas.map(String);
    for (const s of g.series) {
      if (!s || typeof s.nombre !== 'string' || !Array.isArray(s.valores) || s.valores.length !== etiquetas.length) return null;
      if (!s.valores.every((v) => typeof v === 'number' && Number.isFinite(v))) return null;
    }
    return { tipo: g.tipo, etiquetas, series: g.series, ...(g.titulo ? { titulo: String(g.titulo) } : {}), ...(g.unidad ? { unidad: String(g.unidad) } : {}) };
  } catch {
    return null;
  }
}

export interface Opcion {
  titulo: string;
  recomendada: boolean;
  cuerpo: string;
}

/** ```opciones: opciones separadas por una línea «---»; la primera línea de cada una es su título; «(recomendada)» la marca. */
export function leerOpciones(texto: string): Opcion[] {
  return texto
    .split(/^\s*---\s*$/m)
    .map((trozo) => trozo.trim())
    .filter(Boolean)
    .map((trozo) => {
      const [primera = '', ...resto] = trozo.split('\n');
      const limpia = primera.replace(/^#+\s*/, '').trim();
      const recomendada = /\(recomendada\)/i.test(limpia);
      return { titulo: limpia.replace(/\s*\(recomendada\)\s*/i, ' ').trim(), recomendada, cuerpo: resto.join('\n').trim() };
    });
}

/** Título y entradilla para la tarjeta del chat: el primer «# …» y el primer párrafo de texto después. */
export function portada(md: string): { titulo: string | null; entradilla: string | null } {
  const lineas = md.split('\n');
  const i = lineas.findIndex((l) => /^#\s+\S/.test(l));
  const titulo = i >= 0 ? lineas[i]!.replace(/^#\s+/, '').trim() : null;
  let entradilla: string | null = null;
  for (const l of lineas.slice(i + 1)) {
    const s = l.trim();
    if (!s) {
      if (entradilla) break;
      continue;
    }
    if (/^(#|```|\||!\[|>|-{3,})/.test(s)) {
      if (entradilla) break;
      continue;
    }
    entradilla = entradilla ? `${entradilla} ${s}` : s;
  }
  return { titulo, entradilla: entradilla?.replace(/[*_`]/g, '') ?? null };
}

/** ¿Es un informe? (lo que se pinta con el diseño de Venuz en vez de como archivo). */
export function esInforme(a: { mimeType?: string; tipo?: string; displayName?: string; nombre?: string }): boolean {
  const mime = a.mimeType ?? a.tipo ?? '';
  const nombre = a.displayName ?? a.nombre ?? '';
  return mime === 'text/markdown' || /\.md$/i.test(nombre);
}

/** Clases de color de las series (tokens oficina.informe-*; orden fijo, nunca rotan). Una sola serie: el oro de la marca. */
export const SERIES = [
  { relleno: 'fill-oficina-informe-1', trazo: 'stroke-oficina-informe-1', fondo: 'bg-oficina-informe-1' },
  { relleno: 'fill-oficina-informe-2', trazo: 'stroke-oficina-informe-2', fondo: 'bg-oficina-informe-2' },
  { relleno: 'fill-oficina-informe-3', trazo: 'stroke-oficina-informe-3', fondo: 'bg-oficina-informe-3' },
  { relleno: 'fill-oficina-informe-4', trazo: 'stroke-oficina-informe-4', fondo: 'bg-oficina-informe-4' },
] as const;
export const UNA_SERIE = { relleno: 'fill-oficina-acento', trazo: 'stroke-oficina-acento', fondo: 'bg-oficina-acento' } as const;
